import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  approveAgreement,
  getAgreement,
  rejectAgreement
} from '../services/api';
import '../stylesheet/FranchiseAgreement.css';

const AgreementView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [agreement, setAgreement] = useState(null);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [legalTerms, setLegalTerms] = useState([]);
  const [checklistResponses, setChecklistResponses] = useState([]);
  const [showApprovalEffects, setShowApprovalEffects] = useState(false);

  const BASE_URL_UPLOADS = 'https://www.gurukrupaeducation.com/';
  const API_URL = 'https://www.gurukrupaeducation.com/api';

  const fetchAgreement = async () => {
    try {
      setLoading(true);
      const res = await getAgreement(id);
      setAgreement(res.data);
    } catch (err) {
      console.error(err);
      setMessage('Failed to load agreement.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${API_URL}/get_agreement_terms.php`)
      .then((res) => setLegalTerms(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('Failed to load terms', err));

    axios
      .get(`${API_URL}/get_agreement_checklist_responses.php?agreement_id=${id}`)
      .then((res) =>
        setChecklistResponses(Array.isArray(res.data) ? res.data : [])
      )
      .catch((err) => console.error('Failed to load checklist responses', err));
  }, [API_URL, id]);

  useEffect(() => {
    fetchAgreement();
  }, [id]);

  useEffect(() => {
    if (agreement?.status === 'approved' && !showApprovalEffects) {
      setShowApprovalEffects(true);
    }
  }, [agreement, showApprovalEffects]);

  const groupedTerms = useMemo(() => {
    const sections = {};
    legalTerms.forEach((term) => {
      const section = term.section || 'Other';
      if (!sections[section]) sections[section] = [];
      sections[section].push(term);
    });
    return sections;
  }, [legalTerms]);

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const statusClass = (status) => {
    if (status === 'approved') return 'status-approved';
    if (status === 'rejected') return 'status-rejected';
    return 'status-pending';
  };

  const handleApprove = async () => {
    if (!window.confirm('Approve and auto-sign this agreement?')) return;

    try {
      const res = await approveAgreement(id);
      setMessage(res.data.message || 'Agreement approved.');
      setAgreement((prev) => ({
        ...prev,
        status: 'approved',
        signed_pdf_path: res.data.pdf || prev?.signed_pdf_path
      }));
      setShowApprovalEffects(true);
      setTimeout(() => fetchAgreement(), 500);
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.message || 'Approval failed.');
    }
  };

  const handleReject = async () => {
    if (!reason.trim()) {
      alert('Please enter rejection reason.');
      return;
    }

    try {
      const res = await rejectAgreement(id, reason);
      setMessage(res.data.message || 'Agreement rejected.');
      fetchAgreement();
    } catch (err) {
      console.error(err);
      setMessage('Rejection failed.');
    }
  };

  if (loading) {
    return (
      <div className="wrap">
        <div className="legal-paper" style={{ textAlign: 'center', padding: '60px 30px' }}>
          <h2 style={{ marginBottom: '12px' }}>Loading Agreement</h2>
          <p style={{ color: '#64748b' }}>
            Please wait while the agreement record is being prepared.
          </p>
        </div>
      </div>
    );
  }

  if (!agreement) {
    return (
      <div className="wrap">
        <div className="legal-paper" style={{ textAlign: 'center', padding: '60px 30px' }}>
          <h2 style={{ marginBottom: '12px' }}>Agreement Not Found</h2>
          <p style={{ color: '#64748b', marginBottom: '20px' }}>
            The requested agreement could not be located.
          </p>
          <button
            type="button"
            className="btn btn-p"
            onClick={() => navigate('/agreements')}
          >
            Back to List
          </button>
        </div>
      </div>
    );
  }

  const isChecklistAccepted = (item) => {
    const rawValue =
      item?.accepted ??
      item?.is_accepted ??
      item?.checked ??
      item?.value ??
      item?.status;

    if (rawValue === true || rawValue === 1) return true;
    if (typeof rawValue === 'string') {
      const normalized = rawValue.trim().toLowerCase();
      return ['1', 'true', 'yes', 'checked', 'accepted'].includes(normalized);
    }

    return false;
  };
  const pageSteps = [
    'Parties Details',
    'Agreement Clauses',
    'Schedules & Fees',
    'Execution & Sign',
    'Receipt'
  ];

  const viewStyles = `
    .view-shell {
      position: relative;
    }
    .view-banner {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
      margin-bottom: 24px;
      padding: 18px 22px;
      background: linear-gradient(135deg, #7f1d1d 0%, #b91c1c 100%);
      color: #fff;
      border-radius: 10px;
      box-shadow: 0 12px 30px rgba(127, 29, 29, 0.18);
    }
    .view-banner h2 {
      margin: 0 0 6px 0;
      font-size: 28px;
    }
    .view-banner p {
      margin: 0;
      opacity: 0.9;
    }
    .status-chip {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 8px 14px;
      border-radius: 999px;
      text-transform: uppercase;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.08em;
    }
    .status-approved {
      background: #dcfce7;
      color: #166534;
    }
    .status-rejected {
      background: #fee2e2;
      color: #991b1b;
    }
    .status-pending {
      background: #ffedd5;
      color: #c2410c;
    }
    .approval-badge {
      margin: 18px 0 24px;
      display: inline-block;
      padding: 10px 18px;
      border-radius: 999px;
      background: #16a34a;
      color: #fff;
      font-weight: 700;
      animation: slideDown 0.45s ease-out forwards;
    }
    .read-only-note {
      margin-top: 12px;
      color: #64748b;
      font-size: 13px;
      font-style: italic;
    }
    .info-strip {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 14px;
      margin: 18px 0 24px;
    }
    .info-card {
      padding: 14px 16px;
      border: 1px solid #e2e8f0;
      background: #f8fafc;
      border-radius: 8px;
    }
    .info-card strong {
      display: block;
      margin-bottom: 6px;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #475569;
    }
    .display-field {
      display: inline-block;
      min-width: 120px;
      border-bottom: 1px dashed #94a3b8;
      padding: 2px 4px 4px;
      margin: 0 4px;
      font-weight: 600;
      color: #0f172a;
    }
    .display-field.block {
      display: block;
      width: 100%;
      margin: 8px 0 0;
      min-height: 24px;
    }
    .view-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 14px;
    }
    .view-table th,
    .view-table td {
      border: 1px solid #dbe4ee;
      padding: 12px;
      vertical-align: top;
    }
    .view-table th {
      background: #f8fafc;
      text-align: left;
      font-size: 13px;
      color: #475569;
    }
    .term-block {
      margin-bottom: 18px;
    }
    .term-block h4 {
      margin: 0 0 10px;
      color: #7f1d1d;
    }
    .signature-grid-view {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 20px;
      margin-top: 18px;
    }
    .signature-panel {
      border: 1px solid #dbe4ee;
      background: #fff;
      padding: 18px;
      min-height: 320px;
    }
    .signature-panel img {
      max-width: 100%;
      object-fit: contain;
    }
    .signature-title-view {
      margin: 0 0 16px;
      font-weight: 700;
      letter-spacing: 0.06em;
      color: #7f1d1d;
    }
    .photo-frame,
    .signature-frame,
    .stamp-frame {
     

      padding: 12px;
      margin-bottom: 14px;
      text-align: center;
      
    }
      .img-stamp{
      width:300px;
      position:relative;
      top:-120px;
      }

     
      
    .action-box {
      margin-top: 26px;
      padding: 18px;
      border: 1px solid #e2e8f0;
      background: #f8fafc;
      border-radius: 8px;
    }
    .action-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 16px;
      flex-wrap: wrap;
    }
    .rejection-input {
      min-width: 220px;
      padding: 10px 12px;
      border: 1px solid #cbd5e1;
      background: #fff;
      font-size: 14px;
    }
    .action-buttons {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
      align-items: center;
    }
    .message-box {
      margin-top: 14px;
      padding: 12px 14px;
      border-radius: 6px;
      background: #eff6ff;
      color: #1d4ed8;
      border: 1px solid #bfdbfe;
      font-weight: 600;
    }
    .stepper.read-only .step-item {
      cursor: default;
    }
    @keyframes slideDown {
      0% { opacity: 0; transform: translateY(-18px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    @media (max-width: 900px) {
      .info-strip,
      .signature-grid-view {
        grid-template-columns: 1fr;
      }
    }
  `;

  return (
    <div className="wrap">
      <style>{viewStyles}</style>

      <div className="view-shell">
        <div className="view-banner">
          <div>
            <h2>Agreement Review</h2>
            <p>Legal-paper layout aligned with your franchise agreement form UI.</p>
          </div>
          <div className={`status-chip ${statusClass(agreement.status)}`}>
            {agreement.status || 'pending'}
          </div>
        </div>

        <div className="stepper read-only">
          {pageSteps.map((label, idx) => (
            <div key={label} className="step-item active">
              <div className="step-num">{idx + 1}</div>
              <div className="step-label">{label}</div>
            </div>
          ))}
        </div>

        <div className="legal-paper">
          <div className="stamp-header-simulation">
            <h1>GuruKrupa Placement &amp; Education Solutions Pvt Ltd</h1>
            <div className="sub-tag">
              (An International Brand for Adhyatmic, Social and Profit Earning)
            </div>
            <div className="reg-uin">Registration UIN No. U72900MH2021PTC371525</div>
          </div>

          <div className="document-title">Franchise Agreement Review Copy</div>

          {showApprovalEffects ? (
            <div className="approval-badge">AGREEMENT APPROVED &amp; AUTO-SIGNED</div>
          ) : null}

          <div className="info-strip">
            <div className="info-card">
              <strong>Agreement ID</strong>
              <span>#{agreement.id}</span>
            </div>
            <div className="info-card">
              <strong>Agreement Date</strong>
              <span>{formatDate(agreement.agreement_date)}</span>
            </div>
            <div className="info-card">
              <strong>Package</strong>
              <span>{agreement.form_type || '-'}</span>
            </div>
          </div>

          <div className="section active">
            <p className="legal-clause">
              THIS FRANCHISE AGREEMENT is made and entered into on
              <span className="display-field">{formatDate(agreement.agreement_date)}</span>,
              <strong> BY AND BETWEEN:</strong>
            </p>

            <p className="legal-clause" style={{ paddingLeft: '30px' }}>
              <strong>M/s. GURUKRUPA PLACEMENT &amp; EDUCATION SOLUTIONS PVT LTD</strong>,
              a Company duly incorporated and registered under the provisions of the
              Companies Act 2013, having Corporate Identification Number UIN
              U72900MH2021PTC371525, having its business office at Shop no 21,
              Plot No 62, Ghrakul CHS LTD, Gorai No 1, Near Swami Vivekananda School,
              Borivali West, Mumbai - 400092, Maharashtra, India (Hereinafter called
              <strong> "THE FRANCHISOR"</strong>).
            </p>

            <p className="legal-clause">
              <strong>AND</strong>
            </p>

            <p className="legal-clause" style={{ paddingLeft: '30px' }}>
              <strong>Ms/Mr/Smt/Miss:</strong>
              <span className="display-field">{agreement.franchisee_name || '-'}</span>,
              age:
              <span className="display-field">{agreement.age || '-'}</span>,
              Email:
              <span className="display-field">{agreement.email || '-'}</span>,
              residing at:
              <span className="display-field block">{agreement.address || '-'}</span>
              located at
              <span className="display-field">{agreement.location || '-'}</span>,
              India (Hereinafter called <strong>"THE FRANCHISEE"</strong>).
            </p>

            <div className="legal-clause-title">AGREEMENT TERM DETAILS</div>
            <div className="legal-grid-row">
              <div className="legal-field-group">
                <label>Agreement Period</label>
                <div className="display-field block">{agreement.agreement_period || '-'}</div>
              </div>
              <div className="legal-field-group">
                <label>Start Date</label>
                <div className="display-field block">
                  {formatDate(agreement.agreement_start_date)}
                </div>
              </div>
              <div className="legal-field-group">
                <label>End Date</label>
                <div className="display-field block">
                  {formatDate(agreement.agreement_end_date)}
                </div>
              </div>
            </div>
          </div>

          <div className="section active">
            <div className="legal-clause-title">IT IS HEREBY AGREED AND RECORDED AS FOLLOWS</div>
            {legalTerms.length === 0 ? (
              <p className="legal-clause">Loading legal clauses...</p>
            ) : (
              Object.keys(groupedTerms).map((section) => (
                <div key={section} className="term-block">
                  <h4>{section}</h4>
                  <ol style={{ paddingLeft: '22px' }}>
                    {groupedTerms[section].map((term) => (
                      <li
                        key={term.id}
                        style={{ marginBottom: '10px' }}
                        dangerouslySetInnerHTML={{ __html: term.clause_text }}
                      />
                    ))}
                  </ol>
                </div>
              ))
            )}
          </div>

          <div className="section active">
            <div className="legal-clause-title">
              SCHEDULE 'A': COMPENSATION &amp; SHARING SCHEDULE
            </div>

            <p className="legal-clause">
              The FRANCHISEE has paid to the FRANCHISOR a consideration fee of Rs.
              <span className="display-field">{agreement.franchisee_amount || '-'}</span>
              (Rupees
              <span className="display-field">{agreement.franchisee_amount_words || '-'}</span>
              Only), via Cheque / Ref No.
              <span className="display-field">{agreement.cheque_number || '-'}</span>,
              Dated
              <span className="display-field">{formatDate(agreement.cheque_date)}</span>
              drawn on Bank
              <span className="display-field">{agreement.bank_name || '-'}</span>.
            </p>

            <div className="legal-clause-title">REVENUE RATIO LAYERS</div>
            <table className="legal-table">
              <thead>
                <tr>
                  <th style={{ width: '72px' }}>Sr</th>
                  <th>Service Particular Classification</th>
                  <th style={{ width: '220px' }}>Payable Ratio Share</th>
                </tr>
              </thead>
              <tbody>
                <tr className="table-section-row">
                  <td colSpan="3">Recruitment Ratios</td>
                </tr>
                <tr>
                  <td>1</td>
                  <td>Recruitment Solutions (Sourced against any business clients)</td>
                  <td>{agreement.sharing_recruitment || '-'}%</td>
                </tr>

                <tr className="table-section-row">
                  <td colSpan="3">Other HR Managed Matrices</td>
                </tr>
                <tr>
                  <td>2</td>
                  <td>
                    <strong>Other HR Services</strong>
                    <ol style={{ margin: '10px 0 0', paddingLeft: '20px' }}>
                      <li>Recruitments</li>
                      <li>Staffs Training (Complete Training out sources)</li>
                      <li>Industrial Training</li>
                      <li>Payroll</li>
                      <li>Compliance</li>
                      <li>Background Verification</li>
                      <li>Marksheet Verifications</li>
                      <li>Address Verifications</li>
                      <li>Tenant Verification</li>
                      <li>Police Verifications</li>
                      <li>HR Policies</li>
                      <li>Management policies</li>
                      <li>IR</li>
                      <li>PMS</li>
                      <li>HR Audit</li>
                      <li>Management Consultant</li>
                      <li>Complete HR out sources</li>
                      <li>College Campus Drives</li>
                      <li>
                        Hire College batches for Campus selection (Complete Campus
                        placement out sources for College/Institute)
                      </li>
                      <li>Jobs Fair</li>
                      <li>Candidates for event for temporary time</li>
                      <li>Driver Agency</li>
                      <li>Maid Agency</li>
                      <li>Nurse Agency</li>
                      <li>Housekeeping agency</li>
                      <li>Office boys Agency</li>
                      <li>Facilities Management</li>
                      <li>Contract Staffs</li>
                      <li>Security Agency</li>
                      <li>HR Courses Teaching</li>
                    </ol>
                  </td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>2a</td>
                  <td>Client sourced by FRANCHISEE, execution fulfillment by FRANCHISEE</td>
                  <td>90%</td>
                </tr>
                <tr>
                  <td>2b</td>
                  <td>Client sourced by FRANCHISOR, execution fulfillment by FRANCHISEE</td>
                  <td>50%</td>
                </tr>

                <tr className="table-section-row">
                  <td colSpan="3">Instant Payout Enterprise Structures</td>
                </tr>
                <tr>
                  <td>3a</td>
                  <td>Instant settlement terms (No replacement liabilities applied)</td>
                  <td>{agreement.instant_no_replace || '-'}%</td>
                </tr>
                <tr>
                  <td>3b</td>
                  <td>Instant settlement terms (Fulfillment replacements requested)</td>
                  <td>50% Sourced</td>
                </tr>
                <tr>
                  <td>4</td>
                  <td>Recruitment (every month providing Company)</td>
                  <td>50%</td>
                </tr>
                <tr>
                  <td>5</td>
                  <td>For HR. 100%,100%,75% Application form</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>5a</td>
                  <td>
                    Apart from Recruitment, if the FRANCHISEE wants to start any
                    other HR services or any other business activity such as
                    Education, Mega mall, Banking product, Real estate, Securities
                    Services etc., and where no additional cost comes on Gurukrupa
                  </td>
                  <td>No need to pay additional amount</td>
                </tr>
                <tr>
                  <td>5b</td>
                  <td>
                    Apart from Recruitment, if the FRANCHISEE wants to provide any
                    other HR services or any other business activity such as
                    Education, Mega mall, Banking product, Real estate, Securities
                    Services etc., and where additional cost comes on Gurukrupa.
                    Example: Security agency, Skill India, MSCIT course, School,
                    Medical licence etc. Here required additional amount on every
                    new location code.
                  </td>
                  <td>The FRANCHISEE have to pay additional cost</td>
                </tr>
                <tr>
                  <td>6</td>
                  <td>For other than HR business</td>
                  <td>50%</td>
                </tr>
                <tr>
                  <td>6a</td>
                  <td>For billing upto Rs.10k for 1 year</td>
                  <td>75%</td>
                </tr>
                <tr>
                  <td>6b</td>
                  <td>For billing upto 30k for 1 year</td>
                  <td>100%</td>
                </tr>
              </tbody>
            </table>

            <div className="legal-clause-title">JOB PORTAL CONFIGURATION CREDITS</div>
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Portal Type</th>
                  <th>Portal Years</th>
                  <th>Daily Access Hours</th>
                  <th>Annual View Credits</th>
                  <th>Annual Posting Credits</th>
                  <th>Annual Mail Utility</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{agreement.portal_type || '-'}</td>
                  <td>{agreement.portal_years || '-'}</td>
                  <td>{agreement.portal_hours || '-'}</td>
                  <td>{agreement.portal_view || '-'}</td>
                  <td>{agreement.portal_posting || '-'}</td>
                  <td>{agreement.portal_mails || '-'}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="section active">
            <div className="legal-clause-title">DECREE OF ACCEPTANCE CHECKLIST</div>
            <div style={{ marginBottom: '24px' }}>
              {checklistResponses.length === 0 ? (
                <p className="legal-clause">No checklist responses recorded.</p>
              ) : (
                checklistResponses.map((item) => (
                  <div key={item.id} className="legal-checklist-item">
                    <input type="checkbox" checked={isChecklistAccepted(item)} readOnly />
                    <label>
                      <strong>{item.label}</strong>
                    </label>
                  </div>
                ))
              )}
            </div>

            <div className="signature-grid-view">
              <div className="signature-panel">
                <p className="signature-title-view">FRANCHISOR — EXECUTANT</p>

                {agreement.status === 'approved' ? (
                  <>
                    <div className="photo-frame">
                      <div style={{ fontSize: '12px', fontWeight: '700', marginBottom: '8px' }}>
                        AUTHORIZED PHOTO
                      </div>
                      <img
                        src={`${BASE_URL_UPLOADS}assets/franchisor-pic.jpeg`}
                        alt="Franchisor"
                        width="140"
                      />
                    </div>

                    <div className="stamp-frame">
                      <div style={{ fontSize: '12px', fontWeight: '700', marginBottom: '8px' }}>
                        AUTHORIZED SIGNATURE AND COMPANY STAMP
                      </div>
                      <img
                        src={`${BASE_URL_UPLOADS}assets/company_stamp-sign.png`}
                        alt="Company stamp"
                        className="img-stamp"
                        width="140"
                      />
                    </div>

                    <div className="display-field block">
                      Gurukrupa Placement &amp; Education Solutions Pvt Ltd
                    </div>
                  </>
                ) : (
                  <>
                    <p className="legal-clause">
                      Corporate seal, signature, and franchisor verification will be
                      displayed automatically once approval is completed.
                    </p>
                    <div className="read-only-note">
                      Current status: {agreement.status || 'pending'}
                    </div>
                  </>
                )}
              </div>

              <div className="signature-panel">
                <p className="signature-title-view">FRANCHISEE — SIGNATORY</p>
                <div className="photo-frame">
                  <div style={{ fontSize: '12px', fontWeight: '700', marginBottom: '8px' }}>
                    PASSPORT PHOTO MATCH
                  </div>
                  {agreement.photo_path ? (
                    <img
                      src={`${BASE_URL_UPLOADS}${agreement.photo_path}`}
                      alt="Franchisee"
                      width="140"
                    />
                  ) : (
                    <p>No photo uploaded</p>
                  )}
                </div>

                <div className="signature-frame">
                  <div style={{ fontSize: '12px', fontWeight: '700', marginBottom: '8px' }}>
                    SIGNATURE ON RECORD
                  </div>
                  {agreement.signature_path ? (
                    <img
                      src={`${BASE_URL_UPLOADS}${agreement.signature_path}`}
                      alt="Franchisee signature"
                      width="220"
                    />
                  ) : (
                    <p>No signature uploaded</p>
                  )}
                </div>

                <div className="legal-field-group" style={{ marginBottom: 0 }}>
                  <label>Full Signatory Name</label>
                  <div className="display-field block">
                    {agreement.franchisee_title || agreement.franchisee_name || '-'}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '20px', background: '#f8fafc', padding: '15px', border: '1px solid #e2e8f0' }}>
              <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                INSTRUMENT WITNESSES
              </label>
              <div className="legal-grid-row" style={{ margin: '0' }}>
                <div className="legal-field-group">
                  <div className="display-field block">{agreement.witness1 || '-'}</div>
                </div>
                <div className="legal-field-group">
                  <div className="display-field block">{agreement.witness2 || '-'}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="section active">
            <div style={{ border: '2px solid #000', padding: '30px', background: '#fff' }}>
              <div
                style={{
                  fontWeight: 'bold',
                  fontSize: '20px',
                  textDecoration: 'underline',
                  marginBottom: '20px',
                  textAlign: 'center'
                }}
              >
                OFFICIAL COGNIZANCE RECEIPT
              </div>

              <p className="legal-clause">
                RECEIVED from the Franchisee Named:
                <span className="display-field">{agreement.receipt_name || '-'}</span>,
                the formal consideration total of Rs.
                <span className="display-field">{agreement.receipt_amount || '-'}</span>
                (Rupees
                <span className="display-field">{agreement.receipt_amount_words || '-'}</span>
                Only).
              </p>

              <p className="legal-clause">
                Settled through Transaction / Cheque / UPI / NEFT Ref No:
                <span className="display-field">{agreement.receipt_cheque || '-'}</span>,
                Dated
                <span className="display-field">{formatDate(agreement.receipt_date)}</span>
                originated from Banking Entity Institution:
                <span className="display-field">{agreement.receipt_bank || '-'}</span>.
              </p>

              <p className="legal-clause" style={{ fontWeight: 'bold', marginTop: '25px' }}>
                I SAY RECEIVED CLEAR VALUE TOTAL VALUE: Rs.
                <span className="display-field">{agreement.receipt_final_amount || '-'}</span>/-
              </p>

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginTop: '40px', flexWrap: 'wrap' }}>
                <div style={{ width: '45%', minWidth: '260px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '10px' }}>
                    RECEIPT WITNESSES
                  </div>
                  <div className="display-field block">{agreement.receipt_witness1 || '-'}</div>
                  <div className="display-field block">{agreement.receipt_witness2 || '-'}</div>
                </div>

                <div
                  style={{
                    width: '45%',
                    minWidth: '260px',
                    textAlign: 'right',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <span style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    FOR GURUKRUPA PLACEMENT AUTHORIZED DECREE
                  </span>
                  {agreement.status === 'approved' ? (
                    <>
                      <span style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                        Authorized signatory signature registered digitally
                      </span>
                      <img
                        src={`${BASE_URL_UPLOADS}assets/company_stamp-sign.png`}
                        alt="Company stamp"
                        className="img-stamp"
                        width="140"
                      />
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="action-box">
            <div className="action-row">
              <div>
                <strong>Current Status:</strong>{' '}
                <span className={`status-chip ${statusClass(agreement.status)}`}>
                  {agreement.status || 'pending'}
                </span>
                <div className="read-only-note">
                  Review actions stay available only while the agreement is pending.
                </div>
              </div>

              <div className="action-buttons">
                {agreement.status === 'pending' ? (
                  <>
                    <button type="button" className="btn btn-s" onClick={handleApprove}>
                      Approve &amp; Auto Sign
                    </button>
                    <input
                      type="text"
                      className="rejection-input"
                      placeholder="Enter rejection reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <button type="button" className="btn btn-p" onClick={handleReject}>
                      Reject
                    </button>
                  </>
                ) : null}

                {agreement.status === 'approved' && agreement.signed_pdf_path ? (
                  <a
                    href={`https://www.gurukrupaeducation.com/api/download_pdf.php?id=${agreement.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-n"
                    style={{ textDecoration: 'none' }}
                  >
                    Download Signed PDF
                  </a>
                ) : null}

                <button
                  type="button"
                  className="btn btn-p"
                  onClick={() => navigate('/agreements')}
                >
                  Back to List
                </button>
              </div>
            </div>

            {message ? <div className="message-box">{message}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementView;
